/**
 * Minimal toast store. Module-level so `toast()` can be called from anywhere
 * (event handlers, async flows) without React context. `useToast()` subscribes
 * through `useSyncExternalStore`.
 */

export type ToastVariant = "default" | "success" | "warning" | "danger";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Milliseconds before auto-dismiss. `0` keeps the toast until dismissed. Default 4000. */
  duration?: number;
  action?: ToastAction;
}

export interface Toast extends ToastOptions {
  id: string;
}

type Listener = () => void;

const DEFAULT_DURATION = 4000;
const MAX_VISIBLE = 5;

let toasts: readonly Toast[] = [];
let counter = 0;
const listeners = new Set<Listener>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) listener();
}

function clearTimer(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

export function dismissToast(id: string): void {
  clearTimer(id);
  if (!toasts.some((t) => t.id === id)) return;
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function toast(options: ToastOptions): string {
  counter += 1;
  const id = `toast-${counter}`;
  const next: Toast = { ...options, id };
  toasts = [...toasts, next].slice(-MAX_VISIBLE);
  const duration = options.duration ?? DEFAULT_DURATION;
  if (duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismissToast(id), duration),
    );
  }
  emit();
  return id;
}

export function clearToasts(): void {
  for (const id of timers.keys()) clearTimer(id);
  toasts = [];
  emit();
}

export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getToasts(): readonly Toast[] {
  return toasts;
}
